import React, { useEffect, useState, useCallback, useMemo } from "react";
import { api } from "../../services/api";
import grids from "../responsive-fixes/grids.module.css";

interface Agent {
  name: string;
  slug: string;
  description: string;
  status_endpoint: string;
  icon: string;
  category: string;
  schedule?: string;
  status?: "online" | "offline" | "error" | "idle" | "running";
  last_run?: string | null;
  last_seen?: string | null;
}

const glassCard: React.CSSProperties = {
  background: "rgba(255,255,255,0.02)",
  backdropFilter: "blur(60px)",
  WebkitBackdropFilter: "blur(60px)",
  border: "0.5px solid rgba(189,178,255,0.15)",
  borderRadius: 16,
  padding: 22,
  color: "#dde5ff",
  display: "flex",
  flexDirection: "column",
  gap: 14,
};

const STATUS_DOT: Record<string, string> = {
  online: "#cfeda2",
  offline: "#ff6e84",
  error: "#ffd6a5",
  idle: "#cfeda2",
  running: "#bdb2ff",
};

const STATUS_EMOJI: Record<string, string> = {
  online: "🟢",
  idle: "🟢",
  offline: "🔴",
  error: "🟡",
  running: "🟣",
};

const CATEGORIES = ["all", "customer", "sales", "intelligence", "finance"] as const;
type CategoryFilter = (typeof CATEGORIES)[number];

const categoryBadge: React.CSSProperties = {
  display: "inline-block",
  padding: "3px 10px",
  borderRadius: 999,
  fontSize: 10,
  fontWeight: 600,
  textTransform: "uppercase",
  letterSpacing: 0.5,
  background: "rgba(189,178,255,0.12)",
  color: "#bdb2ff",
  border: "0.5px solid rgba(189,178,255,0.25)",
};

const runButton: React.CSSProperties = {
  padding: "10px 16px",
  background: "linear-gradient(135deg, #bdb2ff, #cfeda2)",
  color: "#060e20",
  border: "none",
  borderRadius: 10,
  fontWeight: 600,
  cursor: "pointer",
  fontSize: 13,
};

const filterButton = (active: boolean): React.CSSProperties => ({
  padding: "8px 16px",
  borderRadius: 999,
  fontSize: 12,
  fontWeight: 600,
  cursor: "pointer",
  textTransform: "capitalize",
  border: active
    ? "0.5px solid rgba(189,178,255,0.6)"
    : "0.5px solid rgba(189,178,255,0.15)",
  background: active ? "rgba(189,178,255,0.18)" : "rgba(255,255,255,0.02)",
  color: active ? "#dde5ff" : "rgba(221,229,255,0.6)",
});

function fmt(ts?: string | null): string {
  if (!ts) return "—";
  try {
    const date = new Date(ts);
    if (isNaN(date.getTime())) return ts;
    
    // Format as relative time if recent
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMins / 60);
    const diffDays = Math.floor(diffHours / 24);
    
    if (diffMins < 1) return "just now";
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;
    
    return date.toLocaleDateString();
  } catch {
    return ts;
  }
}

const AgentsHub: React.FC = () => {
  const [agents, setAgents] = useState<Agent[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState<string | null>(null);
  const [filter, setFilter] = useState<CategoryFilter>("all");
  const [lastRefresh, setLastRefresh] = useState<Date | null>(null);

  const load = useCallback(async () => {
    try {
      const res: any = await api.agents.list();
      setAgents(res?.agents || []);
      setError("");
      setLastRefresh(new Date());
    } catch (e: any) {
      setError(e?.message || "Failed to load agents");
      console.error("Failed to load agents:", e);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
    const id = setInterval(load, 30000); // Refresh every 30 seconds
    return () => clearInterval(id);
  }, [load]);

  const runNow = async (slug: string) => {
    if (busy) return; // Prevent multiple simultaneous triggers
    setBusy(slug);
    setError("");
    try {
      await api.agents.trigger(slug);
      // Wait a bit before refreshing to allow status update
      setTimeout(load, 1000);
    } catch (e: any) {
      const errorMsg = e?.response?.data?.error || e?.message || "Trigger failed";
      setError(`Failed to trigger ${slug}: ${errorMsg}`);
      console.error(`Trigger failed for ${slug}:`, e);
    } finally {
      setBusy(null);
    }
  };

  const shown = useMemo(
    () => (filter === "all" ? agents : agents.filter((a) => a.category === filter)),
    [agents, filter],
  );

  const onlineCount = useMemo(
    () => agents.filter(a => a.status === "online" || a.status === "idle").length,
    [agents]
  );

  return (
    <div style={{ padding: 32, minHeight: "100vh", background: "#060e20" }}>
      <div style={{ marginBottom: 24, display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
        <div>
          <h1 style={{ fontFamily: "Manrope", color: "#dde5ff", margin: 0, fontSize: 28 }}>
            Agents Hub
          </h1>
          <p style={{ color: "rgba(221,229,255,0.6)", margin: "4px 0 0", fontSize: 13 }}>
            Central dashboard for all AI agents. Live status, manual triggers. Auto-refreshes every 30s.
          </p>
          {lastRefresh && (
            <p style={{ color: "rgba(221,229,255,0.4)", margin: "2px 0 0", fontSize: 11 }}>
              Last refresh: {lastRefresh.toLocaleTimeString()}
            </p>
          )}
        </div>
        <div style={{ 
          padding: "8px 16px", 
          borderRadius: 8, 
          background: "rgba(189,178,255,0.1)",
          border: "0.5px solid rgba(189,178,255,0.2)",
          fontSize: 13,
          color: "#dde5ff"
        }}>
          {onlineCount}/{agents.length} agents online
        </div>
      </div>

      <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginBottom: 20 }}>
        {CATEGORIES.map((c) => (
          <button
            key={c}
            style={filterButton(filter === c)}
            onClick={() => setFilter(c)}
          >
            {c} {c !== "all" && `(${agents.filter(a => a.category === c).length})`}
          </button>
        ))}
      </div>

      {error && (
        <div
          style={{
            ...glassCard,
            borderColor: "rgba(255,110,132,0.4)",
            color: "#ff6e84",
            marginBottom: 16,
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <span>{error}</span>
          <button 
            onClick={() => setError("")}
            style={{
              background: "transparent",
              border: "none",
              color: "#ff6e84",
              cursor: "pointer",
              fontSize: 16,
              padding: "0 8px",
            }}
          >
            ×
          </button>
        </div>
      )}

      {loading && agents.length === 0 && (
        <div style={glassCard}>
          <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
            <div style={{
              width: 20,
              height: 20,
              border: "2px solid rgba(189,178,255,0.3)",
              borderTop: "2px solid #bdb2ff",
              borderRadius: "50%",
              animation: "spin 1s linear infinite",
            }} />
            Loading agents...
          </div>
        </div>
      )}

      {/* M2 fix (audit): 340px minmax overflows below 340px viewport.
          responsive-grid uses minmax(min(280px,100%), 1fr) so the column
          shrinks below the named min instead of clipping. */}
      <div className={grids["responsive-grid"]} style={{ gap: 18 }}>
        {shown.map((a) => {
          const status = a.status || "offline";
          return (
            <div key={a.slug} style={glassCard}>
              <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 12 }}>
                <div style={{ display: "flex", gap: 12, alignItems: "center" }}>
                  <span className="material-icons" style={{ color: "#bdb2ff", fontSize: 28 }}>
                    {a.icon}
                  </span>
                  <div>
                    <div style={{ fontSize: 16, fontWeight: 600, color: "#dde5ff" }}>{a.name}</div>
                    <span style={categoryBadge}>{a.category}</span>
                  </div>
                </div>
                <span style={{ 
                  fontSize: 12, 
                  color: STATUS_DOT[status] || "#dde5ff", 
                  whiteSpace: "nowrap",
                  display: "flex",
                  alignItems: "center",
                  gap: 4,
                }}>
                  {STATUS_EMOJI[status] || "⚪"} {status}
                </span>
              </div>

              <div style={{ fontSize: 12, color: "rgba(221,229,255,0.7)", lineHeight: 1.5 }}>
                {a.description}
              </div>

              {a.schedule && (
                <div style={{ fontSize: 11, color: "rgba(221,229,255,0.5)" }}>
                  ⏱ {a.schedule}
                </div>
              )}

              <div style={{ 
                display: "flex", 
                justifyContent: "space-between", 
                alignItems: "center",
                marginTop: "auto",
                paddingTop: 8,
                borderTop: "0.5px solid rgba(189,178,255,0.1)"
              }}>
                <div style={{ fontSize: 10, color: "rgba(221,229,255,0.4)" }}>
                  last seen · {fmt(a.last_seen || a.last_run)}
                </div>
                <button
                  style={{ 
                    ...runButton, 
                    opacity: busy === a.slug ? 0.6 : 1,
                    minWidth: 120,
                  }}
                  disabled={busy === a.slug || status === "running"}
                  onClick={() => runNow(a.slug)}
                >
                  {busy === a.slug ? (
                    <span style={{ display: "flex", alignItems: "center", gap: 6 }}>
                      <span style={{
                        width: 12,
                        height: 12,
                        border: "2px solid rgba(6,14,32,0.3)",
                        borderTop: "2px solid #060e20",
                        borderRadius: "50%",
                        animation: "spin 1s linear infinite",
                      }} />
                      Triggering...
                    </span>
                  ) : status === "running" ? "Running..." : "Trigger Run"}
                </button>
              </div>
            </div>
          );
        })}
      </div>

      {shown.length === 0 && !loading && (
        <div style={{ ...glassCard, textAlign: "center", padding: 40 }}>
          <div style={{ fontSize: 16, color: "rgba(221,229,255,0.6)", marginBottom: 8 }}>
            No agents found for category "{filter}"
          </div>
          <button 
            onClick={() => setFilter("all")}
            style={{ ...runButton, background: "rgba(189,178,255,0.2)" }}
          >
            Show all agents
          </button>
        </div>
      )}

      <style>{`
        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  );
};

export default AgentsHub;